import React, { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  FiHome,
  FiUsers,
  FiFolder,
  FiFileText,
  FiBookOpen,
  FiActivity,
  FiDollarSign,
  FiUserCheck,
  FiClipboard,
  FiBriefcase,
  FiPackage,
  FiX,
  FiShield,
  FiClock,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { useDashboardPanel } from "../../context/DashboardPanelContext";
import { useClinicBrand } from "../../utils/clinicLogoSync";
import { getJwtPayload } from "../../utils/jwt";
import { getSidebarSections } from "../../utils/dashboardMenu";
import { getClinicUserDisplayName, getClinicUserRoleLabel } from "../../utils/clinicUserDisplay";

function getClinicInitials(name) {
  if (!name || !name.trim()) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

const MENU_ICONS = {
  home: FiHome,
  patients: FiUsers,
  patientsList: FiUsers,
  cases: FiFolder,
  reports: FiFileText,
  emrs: FiBookOpen,
  laboratory: FiActivity,
  services: FiPackage,
  payments: FiDollarSign,
  staff: FiUserCheck,
  applies: FiClipboard,
  auditLogs: FiShield,
  history: FiClock,
  clinicProfile: FiBriefcase,
  doctorProfile: FiUserCheck,
};

const SIDEBAR_COLLAPSED_KEY = "clinicops_sidebar_collapsed";

function readSidebarCollapsed() {
  try {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (stored == null) return true;
    return stored !== "false";
  } catch {
    return true;
  }
}

function NavItems({ sections, onNavigate, collapsed }) {
  return (
    <nav className="flex-1 px-2 py-1 space-y-0.5 overflow-y-auto" aria-label="Navigimi kryesor">
      {sections.map((section) => (
        <div key={section.label || "rest"}>
          {section.label && !collapsed ? <p className="nav-section-label">{section.label}</p> : null}
          {section.items.map(({ label, icon: Icon, path, tourId }) => (
            <NavLink
              key={`${path}-${label}`}
              to={path}
              end={path === "/dashboard"}
              onClick={onNavigate}
              title={label}
              aria-label={label}
              data-tour={tourId || undefined}
              className={({ isActive }) =>
                `${isActive ? "sidebar-link-active" : "sidebar-link-inactive"} ${
                  collapsed ? "justify-center px-0" : ""
                }`
              }
            >
              <Icon size={collapsed ? 18 : 16} className="shrink-0" strokeWidth={1.75} />
              {collapsed ? null : <span className="truncate">{label}</span>}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}

const Sidebar = ({ mobileOpen = false, onMobileClose }) => {
  const { user, role, clinicMode } = useAuth();
  const { activePanel, roleLower } = useDashboardPanel();
  const hasClinic = !!(user?.clinicId ?? user?.ClinicId);
  const { clinicProfile, logoUrl: clinicLogoUrl } = useClinicBrand(hasClinic);
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        // The menu still toggles for this visit.
      }
      return next;
    });
  };

  const jwtPayload = getJwtPayload();
  const clinicDisplayName =
    clinicProfile?.name ??
    clinicProfile?.Name ??
    jwtPayload?.clinicName ??
    jwtPayload?.ClinicName ??
    user?.clinicName ??
    user?.ClinicName ??
    null;

  const sections = useMemo(() => {
    return getSidebarSections({ roleLower, activePanel, hasClinic, clinicMode }).map((section) => ({
      label: section.label,
      items: section.items.map(({ key, label, path }) => ({
        label,
        path,
        icon: MENU_ICONS[key] || FiHome,
        tourId: key === "clinicProfile" ? "clinic-profile" : key,
      })),
    }));
  }, [roleLower, activePanel, hasClinic, clinicMode]);

  const staffName = getClinicUserDisplayName(user);
  const staffRole = getClinicUserRoleLabel(role);

  const renderSidebar = ({ iconOnly, showMobileClose, showToggle }) => (
    <>
      <div
        className={`border-b border-slate-200 flex gap-2 ${
          iconOnly ? "flex-col items-center px-2 py-2" : "items-center justify-between px-3 py-3"
        }`}
      >
        {showToggle && iconOnly ? (
          <button
            type="button"
            onClick={toggleCollapsed}
            title="Hap menunë"
            aria-label="Hap menunë"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          >
            <FiChevronRight size={18} />
          </button>
        ) : null}
        <div className={`flex items-center min-w-0 ${iconOnly ? "" : "gap-2"}`}>
          {hasClinic ? (
            <>
              {clinicLogoUrl ? (
                <img
                  src={clinicLogoUrl}
                  alt=""
                  title={clinicDisplayName || "Klinika"}
                  className="h-8 w-8 shrink-0 rounded-md object-contain bg-slate-50 border border-slate-200"
                />
              ) : (
                <span
                  title={clinicDisplayName || "Klinika"}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-slate-900 text-white font-semibold text-[11px]"
                >
                  {getClinicInitials(clinicDisplayName || "K")}
                </span>
              )}
              {iconOnly ? null : (
                <div className="min-w-0">
                  <p className="text-[10px] font-medium uppercase tracking-wider text-slate-400">Klinika</p>
                  <p className="text-sm font-semibold text-slate-900 truncate">
                    {clinicDisplayName || "Klinika"}
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              <span
                title="iKlinika"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-slate-900 text-white font-semibold text-[11px]"
              >
                iK
              </span>
              {iconOnly ? null : (
                <span className="text-sm font-semibold text-slate-900 truncate">iKlinika</span>
              )}
            </>
          )}
        </div>
        {showToggle && !iconOnly ? (
          <button
            type="button"
            onClick={toggleCollapsed}
            title="Mbyll menunë"
            aria-label="Mbyll menunë"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          >
            <FiChevronLeft size={18} />
          </button>
        ) : null}
        {showMobileClose && onMobileClose ? (
          <button
            type="button"
            onClick={onMobileClose}
            className="md:hidden p-1.5 rounded-md text-slate-500 hover:bg-slate-100"
            aria-label="Mbyll menunë"
          >
            <FiX size={18} />
          </button>
        ) : null}
      </div>
      <NavItems sections={sections} onNavigate={onMobileClose} collapsed={iconOnly} />
      {iconOnly ? null : (
        <div className="mt-auto border-t border-slate-200 px-3 py-3">
          <p className="text-sm font-medium text-slate-900 truncate">{staffName}</p>
          <p className="text-[11px] text-slate-500 mt-0.5">{staffRole}</p>
        </div>
      )}
    </>
  );

  return (
    <>
      {mobileOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-slate-900/40 md:hidden"
          onClick={onMobileClose}
          aria-label="Mbyll menunë"
        />
      )}

      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-60 max-w-[85vw] bg-white border-r border-slate-200
          flex flex-col transform transition-transform duration-150 ease-out md:hidden
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {renderSidebar({ iconOnly: false, showMobileClose: true, showToggle: false })}
      </aside>

      <aside
        className={`hidden md:flex bg-white border-r border-slate-200 flex-col shrink-0 transition-[width] duration-150 ${
          collapsed ? "w-16" : "w-56 lg:w-60"
        }`}
      >
        {renderSidebar({ iconOnly: collapsed, showMobileClose: false, showToggle: true })}
      </aside>
    </>
  );
};

export default Sidebar;
